import React, { useState, useEffect } from 'react';
import { SystemHealth } from '../components/Dashboard/SystemHealth';
import { CapitalAllocation } from '../components/widgets/CapitalAllocation';
import { TaxRevenue } from '../components/widgets/TaxRevenue';
import { AccumulationStrategy } from '../components/widgets/AccumulationStrategy';
import { AsymmetricGridVisualization } from '../components/widgets/AsymmetricGridVisualization';
import { SupportLevelStatus } from '../components/widgets/SupportLevelStatus';
import { api } from '../utils/api';
import { config, debugLog } from '../utils/config';

export const Dashboard: React.FC = () => {
  const [systemStatus, setSystemStatus] = useState({
    overall: 'healthy' as 'healthy' | 'warning' | 'error',
    onChain: 'healthy' as 'healthy' | 'warning' | 'error',
    offChain: 'healthy' as 'healthy' | 'warning' | 'error',
    lastUpdate: new Date()
  });

  const [engineStatus, setEngineStatus] = useState({
    strategyStatus: 'unknown'
  });

  const [mevStatus, setMevStatus] = useState({
    status: 'inactive' as 'active' | 'inactive' | 'error',
    threatsBlocked24h: 0
  });

  const [webhookStatus, setWebhookStatus] = useState({
    lastReceived: null as number | null,
    totalEvents: 0
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load initial data
  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Test LocalStack connection if enabled
      if (config.localstack.enabled) {
        const connectionTest = await api.testLocalStack();
        if (!connectionTest.connected) {
          debugLog('LocalStack connection failed:', connectionTest.error);
          setError(`LocalStack connection failed: ${connectionTest.error}`);
          return;
        }
        debugLog('LocalStack connection successful');
      }

      // Load only system health data (no trading/price data)
      const [healthData, engineData, mevData, revenueTransactions] = await Promise.all([
        api.health.getHealth(),
        api.tradingEngine.getStatus().catch(err => {
          debugLog('Trading engine status error:', err);
          return { strategyStatus: 'unknown', lastExecution: null };
        }),
        api.mevProtection.getStatus().catch(err => {
          debugLog('MEV protection status error:', err);
          return { status: 'error' as const, threatsBlocked24h: 0, lastCheck: Date.now(), protectionMethods: [] };
        }),
        api.revenue.getTransactions(10).catch(err => {
          debugLog('Revenue transactions error:', err);
          return [];
        })
      ]);

      // Safely parse lastUpdate date, fallback to current time if invalid
      let parsedDate = new Date(healthData.lastUpdate);
      if (isNaN(parsedDate.getTime())) {
        parsedDate = new Date();
      }

      setSystemStatus({
        overall: healthData.overall,
        onChain: healthData.onChain,
        offChain: healthData.offChain,
        lastUpdate: parsedDate
      });

      setEngineStatus({
        strategyStatus: engineData.strategyStatus || 'unknown'
      });

      setMevStatus({
        status: mevData.status,
        threatsBlocked24h: mevData.threatsBlocked24h || 0
      });

      // Process webhook status from revenue transactions
      if (revenueTransactions.length > 0) {
        // Sort by timestamp to get most recent
        const sortedTransactions = [...revenueTransactions].sort((a, b) => {
          const timestampA = typeof a.timestamp === 'number' ? a.timestamp : new Date(a.timestamp).getTime();
          const timestampB = typeof b.timestamp === 'number' ? b.timestamp : new Date(b.timestamp).getTime();
          return timestampB - timestampA;
        });

        const latestTransaction = sortedTransactions[0];
        const lastReceivedTimestamp = typeof latestTransaction.timestamp === 'number'
          ? latestTransaction.timestamp
          : new Date(latestTransaction.timestamp).getTime();

        setWebhookStatus({
          lastReceived: lastReceivedTimestamp,
          totalEvents: revenueTransactions.length
        });
      } else {
        setWebhookStatus({
          lastReceived: null,
          totalEvents: 0
        });
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
      debugLog('Dashboard data loading error:', errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();

    // Set up periodic refresh based on configuration
    const interval = setInterval(loadData, config.intervals.medium);

    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <div className="container-fluid">
        <div className="placeholder-glow mb-4">
          <div className="placeholder col-4 mb-2" style={{ height: '2rem' }}></div>
          <div className="placeholder col-6" style={{ height: '1rem' }}></div>
        </div>
        <div className="row g-3">
          <div className="col-12">
            <div className="card placeholder-glow">
              <div className="card-body">
                <div className="placeholder col-12" style={{ height: '200px' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="mb-4">
        <h1 className="h2 mb-2">Market Maker Dashboard</h1>
        <p className="text-muted">
          System health overview - For trading data and BWS price, visit the <a href="/on-chain" className="text-primary">On-Chain Monitor</a> page
          {config.localstack.enabled && (
            <span className="ms-2 badge bg-primary-subtle text-primary border border-primary">
              <i className="bi bi-hdd-network me-1"></i>
              LocalStack Mode
            </span>
          )}
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger d-flex align-items-start mb-4" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-3 fs-4"></i>
          <div className="flex-grow-1">
            <h5 className="alert-heading mb-1">Connection Error</h5>
            <p className="mb-2">{error}</p>
            <button onClick={loadData} className="btn btn-sm btn-outline-danger">
              <i className="bi bi-arrow-clockwise me-1"></i>
              Retry Connection
            </button>
          </div>
        </div>
      )}

      {/* System Status Card */}
      <div className="row g-3 mb-4">
        <div className="col-12">
          <div className={`card shadow-sm ${systemStatus.overall === 'healthy' ? 'border-success' : 'border-warning'}`}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h3 className="h5 fw-semibold mb-2">System Status</h3>
                  <h2 className={`h1 mb-3 text-capitalize ${systemStatus.overall === 'healthy' ? 'text-success' : 'text-warning'}`}>
                    {systemStatus.overall}
                  </h2>
                  <p className="text-muted mb-0">All market maker systems operational</p>
                </div>
                <div className={`p-3 rounded-circle ${systemStatus.overall === 'healthy' ? 'bg-success-subtle' : 'bg-warning-subtle'}`}>
                  <i className={`bi ${systemStatus.overall === 'healthy' ? 'bi-check-circle' : 'bi-exclamation-triangle'} ${systemStatus.overall === 'healthy' ? 'text-success' : 'text-warning'}`} style={{ fontSize: '3rem' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* System Health - Full Width */}
      <div className="row g-3 mb-4">
        <div className="col-12">
          <SystemHealth
            onChainStatus={systemStatus.onChain}
            offChainStatus={systemStatus.offChain}
            lastUpdate={systemStatus.lastUpdate}
            mevProtectionStatus={mevStatus}
            webhookStatus={webhookStatus}
          />
        </div>
      </div>

      {/* Strategy Status Card */}
      <div className="row g-3 mb-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header bg-transparent">
              <h5 className="card-title mb-0">Market Making Strategy</h5>
              <p className="text-muted small mb-0">Grid trading algorithm status</p>
            </div>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">Grid Strategy Status</p>
                  <h4 className={`mb-0 ${engineStatus.strategyStatus === 'running' || engineStatus.strategyStatus === 'active' ? 'text-success' : engineStatus.strategyStatus === 'error' ? 'text-danger' : 'text-warning'}`}>
                    {engineStatus.strategyStatus === 'running' || engineStatus.strategyStatus === 'active' ? 'Running' : engineStatus.strategyStatus === 'error' ? 'Error' : engineStatus.strategyStatus === 'paused' ? 'Paused' : 'Unknown'}
                  </h4>
                </div>
                <span className={`badge fs-5 ${engineStatus.strategyStatus === 'running' || engineStatus.strategyStatus === 'active' ? 'bg-success' : engineStatus.strategyStatus === 'error' ? 'bg-danger' : 'bg-warning'}`}>
                  {engineStatus.strategyStatus === 'running' || engineStatus.strategyStatus === 'active' ? '✓ Active' : engineStatus.strategyStatus === 'error' ? '✗ Error' : engineStatus.strategyStatus === 'paused' ? '⏸ Paused' : '? Unknown'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Asymmetric Strategy Widgets */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-lg-6">
          <CapitalAllocation />
        </div>
        <div className="col-12 col-lg-6">
          <TaxRevenue />
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-12">
          <AccumulationStrategy />
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-12 col-lg-6">
          <AsymmetricGridVisualization />
        </div>
        <div className="col-12 col-lg-6">
          <SupportLevelStatus />
        </div>
      </div>

      {/* Info Banner - Redirect to On-Chain */}
      <div className="alert alert-info d-flex align-items-start" role="alert">
        <i className="bi bi-info-circle-fill me-3 fs-4"></i>
        <div>
          <h5 className="alert-heading mb-2">Trading Data & Price Information</h5>
          <p className="mb-2">
            BWS token price, 24h/7d/30d variations, trading volume, and recent trades are available on the <strong>On-Chain Monitor</strong> page.
          </p>
          <a href="/on-chain" className="btn btn-sm btn-info">
            <i className="bi bi-graph-up me-1"></i>
            View Trading Data
          </a>
        </div>
      </div>

      {/* Alert Banner */}
      {systemStatus.overall !== 'healthy' && (
        <div className="alert alert-warning d-flex align-items-start" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-3 fs-4"></i>
          <div>
            <h5 className="alert-heading mb-1">System Alert</h5>
            <p className="mb-0">
              Some services are experiencing issues. Check the <a href="/on-chain" className="text-warning text-decoration-underline">On-Chain Monitor</a> page for details.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
